import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {JsonSchema, WidgetName} from '../schema-form.types';
import {resolveWidget} from '../widget-registry';
import {SfInputComponent} from './sf-input.component';
import {SfNumberComponent} from './sf-number.component';
import {SfSelectComponent} from './sf-select.component';
import {SfStatusComponent} from './sf-status.component';
import {SfToggleComponent} from './sf-toggle.component';
import {SfTextareaComponent} from './sf-textarea.component';
import {SfChipsComponent} from './sf-chips.component';
import {SfMultiselectComponent} from './sf-multiselect.component';
import {SfFormulaComponent} from './sf-formula.component';
import {SfBadgesComponent} from './sf-badges.component';
import {SfFileListComponent} from './sf-file-list.component';
import {SfIdsValueComponent} from './sf-ids-value.component';
import {SfIfcDataTypeComponent} from './sf-ifc-data-type.component';

/**
 * Das nackte Blatt-Widget zu einem Schema – ohne Label, Beschreibung und sonstige Rahmung.
 *
 * Ausschließlich die nicht-rekursiven Widgets: `object`, `object-array` und `value-binding`
 * bleiben in {@link SchemaFieldComponent}, weil sie sich selbst wieder Felder rendern lassen.
 *
 * Eigene Komponente, damit **zwei** Aufrufer denselben Satz Widgets bekommen, ohne dass daraus
 * ein Import-Zyklus wird: `app-schema-field` für den Normalfall und `sf-value-binding` für den
 * Festwert-Zweig einer Bindung. Ohne diese Trennung müsste das Bindungs-Widget zurück auf
 * `app-schema-field` zeigen, das es selbst rendert.
 */
@Component({
  selector: 'sf-leaf',
  standalone: true,
  host: {'class': 'block w-full'},
  imports: [
    SfInputComponent,
    SfNumberComponent,
    SfSelectComponent,
    SfStatusComponent,
    SfToggleComponent,
    SfTextareaComponent,
    SfChipsComponent,
    SfMultiselectComponent,
    SfFormulaComponent,
    SfBadgesComponent,
    SfFileListComponent,
    SfIdsValueComponent,
    SfIfcDataTypeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @switch (widget) {
      @case ('select') {
        <sf-select [schema]="schema" [value]="value" [disabled]="disabled"
                   (valueChange)="valueChange.emit($event)"></sf-select>
      }
      @case ('status') {
        <sf-status [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-status>
      }
      @case ('number') {
        <sf-number [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-number>
      }
      @case ('toggle') {
        <sf-toggle [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-toggle>
      }
      @case ('textarea') {
        <sf-textarea [schema]="schema" [value]="value" [disabled]="disabled"
                     (valueChange)="valueChange.emit($event)"></sf-textarea>
      }
      @case ('chips') {
        <sf-chips [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-chips>
      }
      @case ('multiselect') {
        <sf-multiselect [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-multiselect>
      }
      @case ('formula') {
        <sf-formula [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)"></sf-formula>
      }
      @case ('badges') {
        <!-- Anzeige-only: der Wert wird woanders verwaltet, deshalb kein valueChange. -->
        <sf-badges [schema]="schema" [value]="value"></sf-badges>
      }
      @case ('files') {
        <sf-file-list [schema]="schema" [value]="value" [disabled]="disabled"
                      (valueChange)="valueChange.emit($event)"></sf-file-list>
      }
      @case ('ids-value') {
        <sf-ids-value [schema]="schema" [value]="value" [disabled]="disabled"
                      (valueChange)="valueChange.emit($event)"></sf-ids-value>
      }
      @case ('ifc-data-type') {
        <sf-ifc-data-type [schema]="schema" [value]="value" [disabled]="disabled"
                          (valueChange)="valueChange.emit($event)"></sf-ifc-data-type>
      }
      @default {
        <sf-input [schema]="schema" [value]="value" [disabled]="disabled"
                  (valueChange)="valueChange.emit($event)"></sf-input>
      }
    }
  `,
})
export class SfLeafComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Input() disabled = false;
  /**
   * Widget-Name überschreiben. Nötig für den Festwert-Zweig einer Bindung: dort steht in
   * `x-widget` bereits `value-binding`, das eigentliche Eingabefeld muss also aus dem
   * Schema-Aufbau abgeleitet werden statt aus dem Override.
   */
  @Input() widgetOverride: WidgetName | null = null;

  @Output() valueChange = new EventEmitter<any>();

  get widget(): WidgetName {
    return this.widgetOverride ?? resolveWidget(this.schema);
  }
}
